<template>
  <div class="page-container">
    <div class="page-header">
      <div class="page-title">
        현장 데이터 관리
        <span class="count-text">
          {{ selectedSiteName ? selectedSiteName : '현장을 선택하세요' }}
        </span>
      </div>

      <div class="header-buttons">
        <button class="action-btn ghost" @click="$emit('back')">목록으로</button>
      </div>
    </div>

    <div class="content-wrapper">
      <!-- 조회/삭제 영역 -->
      <div class="control-section">
        <div class="control-section-title">조회 / 삭제</div>

        <div class="search-row">
          <div class="field-group site-group">
            <label class="field-label">현장 선택</label>
            <select v-model="selectedSiteId" class="select-input" @change="onChangeSite">
              <option :value="null">현장을 선택하세요</option>
              <option v-for="site in siteOptions" :key="site.id" :value="site.id">
                {{ site.name }}
              </option>
            </select>
          </div>

          <div class="actions search-actions">
            <div class="date-range">
              <div class="date-input">
                <i class="fa-regular fa-calendar icon"></i>
                <input v-model="startAt" type="datetime-local" class="datetime-input" />
              </div>

              <span class="tilde">~</span>

              <div class="date-input">
                <i class="fa-regular fa-calendar icon"></i>
                <input v-model="endAt" type="datetime-local" class="datetime-input" />
              </div>
            </div>

            <button @click="onClickSearch" :disabled="loading">검색</button>
            <button @click="deleteByPeriod" :disabled="loading || !selectedSiteId">
              데이터 삭제
            </button>
          </div>
        </div>

        <!-- 로거 선택 영역 -->
        <div class="logger-filter-section">
          <div class="logger-filter-header">
            <div class="logger-filter-title-wrap">
              <div class="field-label">로거 선택</div>
              <div class="logger-filter-desc">
                초기 진입 시 전체 로거가 자동 선택됩니다.
                필요 시 개별 해제하거나 다시 전체 선택할 수 있습니다.
              </div>
            </div>

            <div class="logger-filter-actions">
              <button
                type="button"
                class="small-action-btn"
                :class="{ active: isAllSelected }"
                @click="selectAllLoggers"
                :disabled="!loggerOptions.length"
              >
                전체 선택
              </button>

              <button
                type="button"
                class="small-action-btn ghost"
                @click="clearLoggerSelection"
                :disabled="!selectedLoggerLineNumbers.length"
              >
                전체 해제
              </button>
            </div>
          </div>

          <div v-if="loggerOptions.length" class="logger-chip-list">
            <button
              v-for="logger in loggerOptions"
              :key="logger.lineNumber"
              type="button"
              class="logger-chip"
              :class="{ active: isLoggerSelected(logger.lineNumber) }"
              @click="toggleLoggerSelection(logger.lineNumber)"
            >
              <span class="logger-chip-name">{{ logger.displayName }}</span>
              <span class="logger-chip-line">({{ logger.lineNumber }})</span>
            </button>
          </div>

          <div v-else class="logger-empty-box">
            선택 가능한 로거가 없습니다
          </div>

          <div class="logger-selection-summary">
            <template v-if="selectedLoggerLineNumbers.length">
              선택 로거 {{ selectedLoggerLineNumbers.length }}개
            </template>
            <template v-else>
              선택된 로거가 없습니다
            </template>
          </div>
        </div>
      </div>

      <!-- 업로드 영역 -->
      <div class="control-section upload-section">
        <div class="control-section-title">CSV 업로드</div>

        <div class="upload-row">
          <div class="actions upload-actions">
            <label class="file-button">
              CSV 선택
              <input
                ref="fileInput"
                type="file"
                accept=".csv,text/csv"
                @change="onFileChange"
                hidden
              />
            </label>

            <button
              class="primary"
              @click="uploadCsv"
              :disabled="uploading || !selectedSiteId || !selectedFile"
            >
              {{ uploading ? '업로드중...' : '선택한 CSV 업로드' }}
            </button>

            <button
              v-if="selectedFile"
              class="ghost-btn"
              @click="clearSelectedFile"
              :disabled="uploading"
            >
              선택 해제
            </button>
          </div>

          <div class="upload-info-panel">
            <div class="upload-info-item">
              <div class="upload-info-label">업로드 대상 현장</div>
              <div class="upload-info-value">
                {{ selectedSiteName || '현장을 먼저 선택하세요' }}
              </div>
            </div>

            <div class="upload-info-item">
              <div class="upload-info-label">선택 파일</div>
              <div class="upload-info-value file-name">
                {{ selectedFile ? selectedFile.name : '선택된 CSV 파일이 없습니다' }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="summary-box">
        <div>사용중 센서 {{ sensorItems.length }}개</div>
        <div>로거 {{ loggerOptions.length }}개</div>
        <div>전체 조회 데이터 {{ totalMeasurementCount }}건</div>
        <div>현재 페이지 {{ measurementItems.length }}건</div>
      </div>

      <div class="section-card">
        <div class="section-title">사용중 센서 목록</div>

        <div class="table-wrap">
          <table v-if="sensorItems.length" class="data-table">
            <colgroup>
              <col style="width: 90px" />
              <col style="width: 180px" />
              <col style="width: 160px" />
              <col style="width: 150px" />
              <col style="width: 220px" />
              <col style="width: 120px" />
              <col style="width: 120px" />
            </colgroup>
            <thead>
              <tr>
                <th>센서 ID</th>
                <th>센서명</th>
                <th>LineNumber</th>
                <th>센서코드</th>
                <th>원본키</th>
                <th>단위</th>
                <th>사용여부</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="sensor in sensorItems" :key="sensor.sensorId">
                <td>{{ sensor.sensorId }}</td>
                <td>{{ sensor.sensorName || '-' }}</td>
                <td>{{ sensor.lineNumber || '-' }}</td>
                <td>{{ sensor.code || '-' }}</td>
                <td>{{ sensor.rawMeasureKey || '-' }}</td>
                <td>{{ sensor.unit || '-' }}</td>
                <td>{{ sensor.useYn ? '사용' : '미사용' }}</td>
              </tr>
            </tbody>
          </table>

          <div v-else class="empty-box">
            표시할 센서가 없습니다
          </div>
        </div>
      </div>

      <div class="section-card">
        <div class="section-title">
          측정 데이터 목록
          <span class="section-count-text">총 {{ totalMeasurementCount }} 건</span>
        </div>

        <div class="table-wrap">
          <table v-if="measurementItems.length" class="data-table">
            <colgroup>
              <col style="width: 100px" />
              <col style="width: 120px" />
              <col style="width: 140px" />
              <col style="width: 220px" />
              <col style="width: 160px" />
              <col style="width: 120px" />
              <col style="width: auto" />
            </colgroup>
            <thead>
              <tr>
                <th>ID</th>
                <th>LineNumber</th>
                <th>센서 ID</th>
                <th>센서명</th>
                <th>측정값</th>
                <th>단위</th>
                <th>측정일시</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in measurementItems" :key="item.id">
                <td>{{ item.id }}</td>
                <td>{{ item.lineNumber || '-' }}</td>
                <td>{{ item.sensorId || '-' }}</td>
                <td>{{ item.sensorName || '-' }}</td>
                <td>{{ item.measureValue ?? '-' }}</td>
                <td>{{ item.unit || '-' }}</td>
                <td>{{ formatDateTime(item.measuredAt) }}</td>
              </tr>
            </tbody>
          </table>

          <div v-else class="empty-box">
            표시할 측정 데이터가 없습니다
          </div>
        </div>

        <PaginationBar
          :currentPage="currentPage"
          :totalPages="totalPages"
          :groupSize="groupSize"
          @change-page="goToPage"
        />
      </div>
    </div>

    <div v-if="loading || uploading" class="loading-overlay">
      <div class="loading-box">
        {{ uploading ? 'CSV 업로드 중입니다...' : '데이터 조회 중입니다...' }}
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api/axios'
import PaginationBar from '@/components/admin/PaginationBar.vue'

export default {
  name: 'SiteSensorMeasurementManage',

  components: {
    PaginationBar,
  },

  props: {
    siteId: {
      type: Number,
      default: null,
    },
    siteName: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      siteOptions: [],
      selectedSiteId: null,
      selectedSiteName: '',

      startAt: '',
      endAt: '',

      sensorItems: [],
      measurementItems: [],

      selectedLoggerLineNumbers: [],

      totalMeasurementCount: 0,
      totalPages: 0,
      currentPage: 0,
      size: 15,
      groupSize: 5,

      loading: false,
      uploading: false,
      selectedFile: null,
    }
  },

  computed: {
    loggerOptions() {
      const map = new Map()

      for (const sensor of this.sensorItems) {
        const lineNumber = sensor?.lineNumber
        if (!lineNumber) continue

        if (!map.has(lineNumber)) {
          const loggerType = sensor?.loggerType || sensor?.sensorLoggerType || 'LOGGER'
          map.set(lineNumber, {
            lineNumber,
            loggerType,
            displayName: `${loggerType}`,
          })
        }
      }

      return Array.from(map.values()).sort((a, b) => Number(a.lineNumber) - Number(b.lineNumber))
    },

    selectedLoggerCount() {
      return this.selectedLoggerLineNumbers.length
    },

    isAllSelected() {
      return (
        this.loggerOptions.length > 0 &&
        this.selectedLoggerLineNumbers.length === this.loggerOptions.length
      )
    },
  },

  async mounted() {
    this.initDateRange()
    await this.fetchSiteOptions()

    if (this.siteId) {
      this.selectedSiteId = this.siteId
      this.selectedSiteName = this.siteName || ''
      await this.fetchSiteSensorMeasurements(true)
    }
  },

  watch: {
    siteId(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.selectedSiteId = newVal
        this.selectedSiteName = this.siteName || ''
        this.currentPage = 0
        this.selectedLoggerLineNumbers = []
        this.fetchSiteSensorMeasurements(true)
      }
    },

    siteName(newVal) {
      this.selectedSiteName = newVal || ''
    },
  },

  methods: {
    initDateRange() {
      const now = new Date()
      const oneWeekAgo = new Date(now)
      oneWeekAgo.setDate(now.getDate() - 7)

      this.startAt = this.toDatetimeLocalValue(oneWeekAgo)
      this.endAt = this.toDatetimeLocalValue(now)
    },

    toDatetimeLocalValue(date) {
      const yyyy = date.getFullYear()
      const mm = String(date.getMonth() + 1).padStart(2, '0')
      const dd = String(date.getDate()).padStart(2, '0')
      const hh = String(date.getHours()).padStart(2, '0')
      const mi = String(date.getMinutes()).padStart(2, '0')
      return `${yyyy}-${mm}-${dd}T${hh}:${mi}`
    },

    toApiDateTime(value) {
      if (!value) return ''
      return value.replace('T', ' ')
    },

    async fetchSiteOptions() {
      try {
        const response = await api.get('/app/site/list', {
          params: {
            deleted: false,
            orderBy: 'siteNameAsc',
          },
        })

        const result = response.data
        this.siteOptions = Array.isArray(result?.result) ? result.result : []
      } catch (error) {
        console.error('현장 목록 조회 실패', error)
        this.siteOptions = []
      }
    },

    async onChangeSite() {
      const found = this.siteOptions.find(site => site.id === Number(this.selectedSiteId))
      this.selectedSiteName = found?.name || ''
      this.currentPage = 0
      this.sensorItems = []
      this.measurementItems = []
      this.selectedLoggerLineNumbers = []
      this.totalMeasurementCount = 0
      this.totalPages = 0

      if (this.selectedSiteId) {
        await this.fetchSiteSensorMeasurements(true)
      }
    },

    isLoggerSelected(lineNumber) {
      return this.selectedLoggerLineNumbers.includes(lineNumber)
    },

    toggleLoggerSelection(lineNumber) {
      if (this.selectedLoggerLineNumbers.includes(lineNumber)) {
        this.selectedLoggerLineNumbers = this.selectedLoggerLineNumbers.filter(
          item => item !== lineNumber
        )
      } else {
        this.selectedLoggerLineNumbers = [...this.selectedLoggerLineNumbers, lineNumber]
      }
    },

    selectAllLoggers() {
      this.selectedLoggerLineNumbers = this.loggerOptions.map(item => item.lineNumber)
    },

    clearLoggerSelection() {
      this.selectedLoggerLineNumbers = []
    },

    syncAllLoggersIfNeeded(forceSelectAll = false) {
      if (!this.loggerOptions.length) return

      if (forceSelectAll || !this.selectedLoggerLineNumbers.length) {
        this.selectedLoggerLineNumbers = this.loggerOptions.map(item => item.lineNumber)
      }
    },

    onClickSearch() {
      this.currentPage = 0
      this.fetchSiteSensorMeasurements(false)
    },

    async fetchSiteSensorMeasurements(forceSelectAll = false) {
      if (!this.selectedSiteId) {
        alert('현장을 선택해주세요.')
        return
      }

      if (!this.startAt || !this.endAt) {
        alert('시작일시와 종료일시를 입력해주세요.')
        return
      }

      this.loading = true

      try {
        const params = {
          siteId: this.selectedSiteId,
          startAt: this.toApiDateTime(this.startAt),
          endAt: this.toApiDateTime(this.endAt),
          page: this.currentPage,
          size: this.size,
        }

        if (this.selectedLoggerLineNumbers.length) {
          params.loggerLineNumbers = this.selectedLoggerLineNumbers
        }

        const response = await api.get('/app/site/siteSensorMeasurement/list', {
          params,
          paramsSerializer: {
            indexes: null,
          },
        })

        const result = response.data?.result
        const measurementPage = result?.measurementPage

        this.selectedSiteName = result?.siteName || this.selectedSiteName
        this.sensorItems = Array.isArray(result?.sensors) ? result.sensors : []

        this.syncAllLoggersIfNeeded(forceSelectAll)

        this.measurementItems = Array.isArray(measurementPage?.content)
          ? measurementPage.content
          : []

        this.totalMeasurementCount = Number(
          measurementPage?.totalElements ?? this.measurementItems.length
        )
        this.totalPages = Number(measurementPage?.totalPages ?? 0)
        this.currentPage = Number(measurementPage?.number ?? this.currentPage)

        if (
          this.currentPage > 0 &&
          this.measurementItems.length === 0 &&
          this.totalMeasurementCount > 0
        ) {
          this.currentPage = this.currentPage - 1
          await this.fetchSiteSensorMeasurements(false)
          return
        }

        console.log('siteSensorMeasurement response.data =', response.data)
        console.log('measurementItems =', this.measurementItems)
      } catch (error) {
        console.error('현장 데이터 조회 실패', error)
        this.sensorItems = []
        this.measurementItems = []
        this.totalMeasurementCount = 0
        this.totalPages = 0
        alert('현장 데이터 조회에 실패했습니다.')
      } finally {
        this.loading = false
      }
    },

    goToPage(pageNo) {
      if (pageNo < 0 || pageNo >= this.totalPages || pageNo === this.currentPage) {
        return
      }

      this.currentPage = pageNo
      this.fetchSiteSensorMeasurements(false)
    },

    async deleteByPeriod() {
      if (!this.selectedSiteId) {
        alert('현장을 선택해주세요.')
        return
      }

      if (!this.startAt || !this.endAt) {
        alert('시작일시와 종료일시를 입력해주세요.')
        return
      }

      const loggerText = this.selectedLoggerLineNumbers.length
        ? `\n선택 로거 ${this.selectedLoggerLineNumbers.length}개만 삭제합니다.`
        : '\n전체 로거 데이터를 삭제합니다.'

      const ok = window.confirm(`선택한 기간의 데이터를 삭제하시겠습니까?${loggerText}`)
      if (!ok) return

      this.loading = true

      try {
        const requestBody = {
          siteId: this.selectedSiteId,
          startAt: new Date(this.startAt).toISOString(),
          endAt: new Date(this.endAt).toISOString(),
        }

        if (this.selectedLoggerLineNumbers.length) {
          requestBody.loggerLineNumbers = this.selectedLoggerLineNumbers
        }

        const response = await api.post('/app/site/siteSensorMeasurement/deleteByPeriod', requestBody)

        const deletedCount = response.data?.result ?? 0
        alert(`데이터 삭제가 완료되었습니다. (${deletedCount}건)`)

        this.currentPage = 0
        await this.fetchSiteSensorMeasurements(false)
      } catch (error) {
        console.error('데이터 삭제 실패', error)
        alert(error?.response?.data?.message || '데이터 삭제에 실패했습니다.')
      } finally {
        this.loading = false
      }
    },

    onFileChange(event) {
      const file = event.target.files?.[0]
      if (!file) return

      const lowerName = file.name.toLowerCase()
      if (!lowerName.endsWith('.csv')) {
        alert('CSV 파일만 업로드할 수 있습니다.')
        event.target.value = ''
        this.selectedFile = null
        return
      }

      this.selectedFile = file
    },

    clearSelectedFile() {
      this.selectedFile = null

      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
    },

    async uploadCsv() {
      if (!this.selectedSiteId) {
        alert('현장을 선택해주세요.')
        return
      }

      if (!this.selectedFile) {
        alert('CSV 파일을 선택해주세요.')
        return
      }

      const formData = new FormData()
      formData.append('siteId', this.selectedSiteId)
      formData.append('file', this.selectedFile)

      this.uploading = true

      try {
        const response = await api.post('/app/site/siteSensorMeasurement/uploadCsv', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })

        const result = response.data?.result

        alert(
          `CSV 업로드 완료\n` +
            `lineNumber: ${result?.lineNumber ?? '-'}\n` +
            `읽은 행: ${result?.readRowCount ?? 0}건\n` +
            `저장 건수: ${result?.savedMeasurementCount ?? 0}건\n` +
            `생성 센서 수: ${result?.sensorCreatedCount ?? 0}개`
        )

        this.clearSelectedFile()
        this.currentPage = 0
        await this.fetchSiteSensorMeasurements(true)
      } catch (error) {
        console.error('CSV 업로드 실패', error)
        alert(error?.response?.data?.message || 'CSV 업로드에 실패했습니다.')
      } finally {
        this.uploading = false
      }
    },

    formatDateTime(value) {
      if (!value) return '-'

      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return value

      const yy = String(date.getFullYear()).slice(-2)
      const mm = String(date.getMonth() + 1).padStart(2, '0')
      const dd = String(date.getDate()).padStart(2, '0')
      const hh = String(date.getHours()).padStart(2, '0')
      const mi = String(date.getMinutes()).padStart(2, '0')
      const ss = String(date.getSeconds()).padStart(2, '0')

      return `${yy}-${mm}-${dd} ${hh}:${mi}:${ss}`
    },
  },
}
</script>

<style scoped>
@import "@/styles/admin/admin-layout.css";
@import "@/styles/admin/admin-table.css";
@import "@/styles/admin/admin-button.css";
@import "@/styles/admin/admin-input.css";
@import "@/styles/admin/admin-pagination.css";

.page-container {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 20px;
  box-sizing: border-box;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  margin-bottom: 20px;
  background-color: var(--color-white);
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.page-title {
  font-size: 18px;
  font-weight: 600;
  color: #111827;
}

.count-text {
  margin-left: 8px;
  font-size: 14px;
  font-weight: 400;
  color: #6b7280;
}

.content-wrapper {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  background-color: var(--color-white);
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  padding: 16px;
  box-sizing: border-box;
}

.control-section {
  margin-bottom: 16px;
  padding: 16px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fcfcfd;
}

.upload-section {
  background: #fffaf1;
  border-color: #f1ddba;
}

.control-section-title {
  margin-bottom: 12px;
  font-size: 14px;
  font-weight: 700;
  color: #111827;
}

.search-row,
.upload-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 14px;
}

.field-group {
  display: flex;
  align-items: center;
  gap: 10px;
}

.field-label {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}

.select-input {
  min-width: 320px;
  height: 40px;
  padding: 0 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #fff;
}

.actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.search-actions {
  flex: 1;
}

.upload-actions {
  flex-shrink: 0;
}

.date-range {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.date-input {
  display: flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 10px;
  background-color: var(--color-white);
  border: 1px solid var(--color-border);
  border-radius: 8px;
}

.icon {
  font-size: 14px;
  color: var(--color-text-sub);
}

.datetime-input {
  width: 170px;
  height: 100%;
  border: none;
  background: transparent;
  font-size: 13px;
  color: var(--color-text-main);
}

.datetime-input:focus {
  outline: none;
}

.date-input:focus-within {
  border-color: var(--color-primary-border);
  box-shadow: 0 0 0 1px color-mix(in srgb, var(--color-primary) 35%, transparent);
}

.tilde {
  font-weight: 600;
  color: var(--color-text-sub);
}

.logger-filter-section {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px dashed #e5e7eb;
}

.logger-filter-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.logger-filter-title-wrap {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.logger-filter-desc {
  font-size: 12px;
  color: #6b7280;
}

.logger-filter-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.small-action-btn {
  height: 32px;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 500;
  color: var(--color-text-main);
  background-color: var(--color-white);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  cursor: pointer;
}

.small-action-btn.ghost {
  background: #f9fafb;
}

.small-action-btn.active {
  background: #fff4df;
  border-color: #f0b860;
  color: #b45309;
}

.logger-chip-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.logger-chip {
  min-height: 38px;
  padding: 0 14px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #d1d5db;
  border-radius: 999px;
  background: #fff;
  color: #374151;
  cursor: pointer;
  transition: all 0.2s ease;
}

.logger-chip:hover {
  background: #f9fafb;
}

.logger-chip.active {
  background: #fff4df;
  border-color: #f0b860;
  color: #b45309;
}

.logger-chip-name {
  font-size: 13px;
  font-weight: 600;
}

.logger-chip-line {
  font-size: 12px;
  color: inherit;
}

.logger-empty-box {
  min-height: 44px;
  display: flex;
  align-items: center;
  padding: 0 12px;
  border: 1px dashed #d1d5db;
  border-radius: 10px;
  color: #8a94a6;
  background: #fff;
}

.logger-selection-summary {
  margin-top: 10px;
  font-size: 13px;
  color: #475569;
}

.actions button,
.file-button,
.ghost-btn {
  height: 36px;
  padding: 0 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 500;
  color: var(--color-text-main);
  background-color: var(--color-white);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
}

.actions button:hover,
.file-button:hover,
.ghost-btn:hover {
  background-color: var(--color-bg-hover);
}

.actions button.primary {
  background-color: var(--color-primary);
  border-color: var(--color-primary);
  color: var(--color-white);
}

.upload-info-panel {
  flex: 1;
  min-width: 320px;
  display: grid;
  grid-template-columns: repeat(2, minmax(220px, 1fr));
  gap: 10px;
}

.upload-info-item {
  min-height: 58px;
  padding: 10px 12px;
  border: 1px solid #eadfc9;
  border-radius: 10px;
  background: #ffffff;
}

.upload-info-label {
  margin-bottom: 4px;
  font-size: 12px;
  font-weight: 700;
  color: #92400e;
}

.upload-info-value {
  font-size: 13px;
  color: #374151;
  word-break: break-all;
}

.file-name {
  font-weight: 600;
}

.summary-box {
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
  margin-bottom: 18px;
  padding: 14px 16px;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  font-size: 14px;
  color: #334155;
}

.section-card {
  margin-bottom: 18px;
}

.section-title {
  margin-bottom: 10px;
  font-size: 15px;
  font-weight: 700;
  color: #111827;
  display: flex;
  align-items: center;
  gap: 8px;
}

.section-count-text {
  font-size: 13px;
  font-weight: 400;
  color: #6b7280;
}

.table-wrap {
  width: 100%;
  overflow-x: auto;
}

.data-table {
  width: 100%;
  min-width: 980px;
  border-collapse: collapse;
}

.empty-box {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 160px;
  color: #8a94a6;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
}

.loading-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.loading-box {
  min-width: 260px;
  padding: 20px 24px;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);
  font-size: 15px;
  font-weight: 600;
  color: #111827;
  text-align: center;
}

.header-buttons {
  display: flex;
  gap: 8px;
}

@media (max-width: 1200px) {
  .upload-info-panel {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 900px) {
  .search-row,
  .upload-row {
    flex-direction: column;
    align-items: stretch;
  }

  .field-group {
    flex-direction: column;
    align-items: stretch;
  }

  .select-input {
    min-width: 100%;
    width: 100%;
  }

  .search-actions,
  .upload-actions {
    width: 100%;
  }

  .logger-filter-header {
    flex-direction: column;
    align-items: stretch;
  }
}

/* =========================
   TABLE ROW ACTIVE
========================= */

.data-table tbody tr {
  transition:
    background-color 0.15s ease,
    box-shadow 0.15s ease;
}

/* hover 시 행 강조 */
.data-table tbody tr:hover td {
  background: #f8fafc;
}

/* 좌측 active bar */
.data-table tbody tr:hover {
  box-shadow: inset 3px 0 0 #e58a13;
}
</style>